import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ClipboardIcon, ClipboardDocumentCheckIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useCalculator } from '../context/CalculatorContext';
import MDEComparisonTable from './MDEComparisonTable';

const ResultsDisplay: React.FC = () => {
  const { 
    result, 
    formState, 
    clipboardState,
    copySamplesPerVariant,
    copyTotalSampleSize,
    copyShareUrl
  } = useCalculator();
  const [tipsOpen, setTipsOpen] = useState(false);
  const [understandOpen, setUnderstandOpen] = useState(true);

  if (!result) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
        <p>Please check your input values and try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
        <h3 className="font-medium text-lg mb-2">Sample Size Requirements</h3>
        {/* Responsive: stack all on mobile, two side-by-side on md/lg, all three side-by-side on xl+ */}
        <div className="flex flex-col md:flex-row md:gap-4 xl:gap-4">
          <div className="flex-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">Samples per variant</p>
            <div className="flex items-center">
              <p className="text-2xl font-bold">{result.samplesPerVariant.toLocaleString()}</p>
              <button 
                onClick={copySamplesPerVariant}
                className="ml-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                title="Copy samples per variant to clipboard"
              >
                {clipboardState.samplesPerVariantCopied ? (
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <ClipboardIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          <div className="flex-1 mt-4 md:mt-0">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total sample size</p>
            <div className="flex items-center">
              <p className="text-2xl font-bold">{result.totalSampleSize.toLocaleString()}</p>
              <button 
                onClick={copyTotalSampleSize}
                className="ml-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                title="Copy total sample size to clipboard"
              >
                {clipboardState.totalSampleSizeCopied ? (
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <ClipboardIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {/* Show estimated days to run only on xl and above */}
          <div className="flex-1 mt-4 xl:mt-0 hidden xl:block">
            <p className="text-sm text-gray-500 dark:text-gray-400">Estimated days to run</p>
            <p className="text-2xl font-bold">{result.daysToRun.toLocaleString()}</p>
          </div>
        </div>
        {/* On md and lg, show estimated days to run as a separate row below the two stats; hide on mobile and xl+ */}
        <div className="hidden md:block xl:hidden mt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Estimated days to run</p>
          <p className="text-2xl font-bold">{result.daysToRun.toLocaleString()}</p>
        </div>
        {/* On mobile, show estimated days to run as a separate row below the two stats; hide on md+ */}
        <div className="block md:hidden mt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Estimated days to run</p>
          <p className="text-2xl font-bold">{result.daysToRun.toLocaleString()}</p>
        </div>
        
        <div className="flex justify-center mt-4">
          <button
            onClick={copyShareUrl}
            className="flex items-center space-x-2 px-3 py-1.5 bg-white dark:bg-blue-800 text-blue-600 dark:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-700 transition-colors shadow-sm"
            title="Copy shareable URL to clipboard"
          >
            {clipboardState.urlCopied ? (
              <>
                <ClipboardDocumentCheckIcon className="h-4 w-4" />
                <span>Copied to Clipboard!</span>
              </>
            ) : (
              <>
                <LinkIcon className="h-4 w-4" />
                <span>Save Test Configuration</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Move Understand What This Means above MDE Comparison Table */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
        <button
          type="button"
          className="w-full flex items-center justify-between mb-2 px-0 py-0 bg-transparent border-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded transition cursor-pointer"
          aria-expanded={understandOpen}
          aria-controls="understand-section"
          onClick={() => setUnderstandOpen((open) => !open)}
        >
          <h3 className="font-medium m-0 text-left">Understand What This Means</h3>
          {understandOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400 ml-2" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2" />
          )}
          <span className="sr-only">{understandOpen ? 'Hide' : 'Show'} details</span>
        </button>
        {understandOpen && (
          <div id="understand-section">
            <p className="text-sm"> To detect a <strong>{formState.mde}%</strong> relative change with <strong>{formState.confidenceLevel}%</strong> confidence:</p>
            <ul className="space-y-2 text-sm list-disc p-4">
              <li>You need <strong>{result.samplesPerVariant.toLocaleString()}</strong> samples per variant (assuming equal traffic allocation)</li>
              <li>With <strong>{formState.variants}</strong> variant{formState.variants > 1 ? 's' : ''}, your total required sample size is <strong>{result.totalSampleSize.toLocaleString()}</strong></li>
              <li>With <strong>{formState.dailySamples.toLocaleString()}</strong> daily samples, your test will take approximately <strong>{result.daysToRun}</strong> days</li>
            </ul>
          </div>
        )}
      </div>
      {/* MDE Comparison Table: below What This Means */}
      <MDEComparisonTable formState={formState} selectedMDE={formState.mde} />
      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
        <button
          type="button"
          className="w-full flex items-center justify-between mb-2 px-0 py-0 bg-transparent border-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded transition cursor-pointer"
          aria-expanded={tipsOpen}
          aria-controls="experiment-tips-section"
          onClick={() => setTipsOpen((open) => !open)}
        >
          <h3 className="font-medium m-0 text-left">Explore Tips That Improve Test Results</h3>
          {tipsOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400 ml-2" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2" />
          )}
          <span className="sr-only">{tipsOpen ? 'Hide' : 'Show'} tips</span>
        </button>
        {tipsOpen && (
          <ul
            id="experiment-tips-section"
            className="space-y-2 text-sm list-disc p-4"
          >
            <li><strong>Consistent Data Processing:</strong> Apply the same cleaning and transformation methods to your metrics before calculating sample size and after collecting results - inconsistent processing can lead to invalid conclusions</li>
            <li><strong>User Segments:</strong> Ensure each user segment has sufficient sample size - different segments may respond differently to changes</li>
            <li><strong>Parameter Tradeoffs:</strong> If you consider reducing confidence/power for smaller sample size, try to find more impactful changes with larger effect sizes first</li>
            <li><strong>Traffic Allocation:</strong> Verify traffic is accurately divided between test groups - consider uneven allocation (e.g., 90/10) for risky changes</li>
            <li><strong>Test Duration:</strong> Account for seasonal effects and business cycles - run tests long enough to capture natural metric variation</li>
            <li><strong>Avoid Peeking:</strong> Don't stop tests early based on preliminary results - this dramatically increases false positive rates</li>
            <li><strong>Evidence-Based Patterns:</strong> Build on established design patterns and leverage existing research data before testing - sites like <a href="https://goodui.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition">goodui.org</a> are a great starting point</li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default ResultsDisplay;
